import { mergeAttributes } from "@tiptap/core"
import BulletList from "@tiptap/extension-bullet-list"
import Code from "@tiptap/extension-code"
import CodeBlock from "@tiptap/extension-code-block"
import Details from "@tiptap/extension-details"
import { DetailsContent } from "@tiptap/extension-details-content"
import { DetailsSummary } from "@tiptap/extension-details-summary"
import HeadingExtension from "@tiptap/extension-heading"
import HorizontalRule from "@tiptap/extension-horizontal-rule"
import ImageExtension from "@tiptap/extension-image"
import ItalicExtension from "@tiptap/extension-italic"
import Link from "@tiptap/extension-link"
import OrderedList from "@tiptap/extension-ordered-list"
import TaskItem from "@tiptap/extension-task-item"
import TaskList from "@tiptap/extension-task-list"
import Typography from "@tiptap/extension-typography"
import StarterKit from "@tiptap/starter-kit"

import type { Extensions } from "@tiptap/core"
import { DisableCommandEnter } from "./extensions"
import { CalloutVariantExtension } from "./extensions/callout-variant"
import { CardExtensions } from "./extensions/card-extension"
import { ChangelogAreaExtension } from "./extensions/changelog-area-extension"
import { CodeGroupExtension } from "./extensions/code-group-extension"
import { CustomTaskItemExtension } from "./extensions/custom-task-item-extension"
import { CustomTaskListExtension } from "./extensions/custom-task-list-extension"
import { DetailsClickHandlerExtension } from "./extensions/details-click-handler-extension"
import { DragSourceExtension } from "./extensions/drag-source-extension"
import { EmExtension } from "./extensions/em-extension"
import { EnterAsBreakExtension } from "./extensions/enter-as-break-extension"
import { HardBreakCaretFix } from "./extensions/hard-break-caret-fix"
import { HighlightExtension } from "./extensions/highlight-extension"
import { ImageGalleryExtension } from "./extensions/image-gallery-extension"
import { InlineImageExtension } from "./extensions/inline-image-extension"
import { AccordionExtensions } from "./extensions/mintlify/accordion"
import { BadgeExtensions } from "./extensions/mintlify/badge"
import { ColorExtensions } from "./extensions/mintlify/color"
import { ExpandableExtensions } from "./extensions/mintlify/expandable"
import { FieldsExtensions } from "./extensions/mintlify/fields"
import { FrameExtensions } from "./extensions/mintlify/frame"
import { IconExtensions } from "./extensions/mintlify/icon"
import { MermaidExtensions } from "./extensions/mintlify/mermaid"
import { PromptExtensions } from "./extensions/mintlify/prompt"
import { TileExtensions } from "./extensions/mintlify/tile"
import { TooltipExtensions } from "./extensions/mintlify/tooltip"
import { TreeExtensions } from "./extensions/mintlify/tree"
import { UpdateExtensions } from "./extensions/mintlify/update"
import { PlainTextPaste } from "./extensions/plain-text-paste"
import { StrongExtension } from "./extensions/strong-extension"
import { TabExtension, TabsExtension } from "./extensions/tabs-extension"
import { ToggleableBlockquote } from "./extensions/toggle-blockquote"

// Tiptap's image node knows src, alt and title only, so a resized or
// background-removed image loses the rest every time its HTML is parsed.
export const ImageWithLayout = ImageExtension.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			width: { default: null },
			height: { default: null },
			"data-original-src": { default: null },
			"data-processed-src": { default: null },
		}
	},
})

const LANGUAGE_CLASS = /(?:^|\s)language-(\S+)/

// The server-side DOM has no firstElementChild, so Tiptap's own language parse
// returns null and a code block loses its highlighting the first time it parses.
const CodeBlockWithLanguage = CodeBlock.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			// A code group labels its tabs with this, so it has to survive every
			// HTML round trip the body makes.
			title: {
				default: null,
				parseHTML: (element: HTMLElement) => element.getAttribute("data-title"),
				renderHTML: (attributes: Record<string, unknown>) =>
					attributes.title ? { "data-title": String(attributes.title) } : {},
			},
			// The reader-side highlighter and the code-group tab label both read
			// `data-language` off the `<pre>`, so it renders as well as parses.
			language: {
				default: null,
				parseHTML: (element: HTMLElement) => {
					const attribute = element.getAttribute("data-language")
					if (attribute) return attribute
					const code = element.querySelector?.("code")
					const classNames = `${code?.getAttribute("class") ?? ""} ${element.getAttribute("class") ?? ""}`
					return LANGUAGE_CLASS.exec(classNames)?.[1] ?? null
				},
				renderHTML: (attributes: Record<string, unknown>) =>
					attributes.language
						? { "data-language": String(attributes.language) }
						: {},
			},
		}
	},
})

export function getDefaultExtensions({
	image = true,
	inlineImage = image,
	heading = true,
	history = true,
	enableCmdEnter = false,
	enterAsBreakeExtension = false,
	bulletListAccentColor = false,
	code = true,
	codeBlock = true,
	orderedList = true,
	link = true,
	imageGallery = true,
	panels = true,
}: {
	image?: boolean
	// On where the editor brings its own block image node but still reads
	// icons that sit inside a sentence.
	inlineImage?: boolean
	heading?: boolean
	history?: boolean
	enableCmdEnter?: boolean
	enterAsBreakeExtension?: boolean
	bulletListAccentColor?: boolean
	code?: boolean
	codeBlock?: boolean
	orderedList?: boolean
	link?: boolean
	imageGallery?: boolean
	// Off where the editor swaps in its own node views for the tabs and
	// code-group blocks.
	panels?: boolean
}): Extensions {
	const exts: Extensions = [
		StarterKit.configure({
			blockquote: false,
			bulletList: false,
			orderedList: false,
			codeBlock: false,
			code: code ? {} : false,
			horizontalRule: false,

			italic: false,
			...(history ? {} : { history: false }), // disable history (undo/redo)
			heading: false,
			// Same weight as the docs nav insertion line, and it follows the theme.
			dropcursor: {
				color: "var(--text-primary)",
				width: 2,
				class: "pl-dropcursor",
			},
		}),
		DragSourceExtension,
		ItalicExtension.extend({
			addKeyboardShortcuts() {
				return {
					// ↓ your new keyboard shortcut
					"Mod-i": () => {
						return this.editor.commands.toggleItalic()
					},
					"Mod-I": () => {
						return false
					},
				}
			},
		}),

		CalloutVariantExtension,
		HardBreakCaretFix,
		PlainTextPaste,
		HighlightExtension.configure({ multicolor: true }),
		ChangelogAreaExtension,
		CustomTaskItemExtension.configure({}),
		CustomTaskListExtension.configure({}),
		EmExtension.configure({}),
		StrongExtension.configure({}),
		Typography.configure({}),
		TaskItem.configure({
			nested: true,
		}),
		HorizontalRule.extend({
			addKeyboardShortcuts() {
				return {
					"Alt-Shift-/": () => this.editor.commands.setHorizontalRule(),
				}
			},
		}),
		ToggleableBlockquote,
		TaskList,
		Details.configure({
			persist: true,
			HTMLAttributes: {
				class: "details-wrapper",
			},
		}),
		DetailsSummary.configure({
			HTMLAttributes: {
				class: "details-summary",
			},
		}),
		DetailsContent.configure({
			HTMLAttributes: {
				class: "details-content",
			},
		}),
		DetailsClickHandlerExtension,
		BulletList.extend({
			addAttributes() {
				return {
					// Parsed as well as rendered: a doc read back through the schema
					// (proposal steps, markdown round-trips) keeps the house class on
					// surfaces that don't force it themselves.
					accentBulletColor: {
						default: "",
						parseHTML: (element: HTMLElement) =>
							element.classList?.contains("accent-bullets")
								? "accent-bullets"
								: "",
						renderHTML: (attributes: Record<string, unknown>) => ({
							class:
								bulletListAccentColor ||
								attributes.accentBulletColor === "accent-bullets"
									? "accent-bullets"
									: null,
						}),
					},
				}
			},
			// @ts-expect-error nice ts
			renderHtml({ HTMLAttributes }) {
				return [
					"ul",
					// eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
					mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
					0,
				]
			},
			addKeyboardShortcuts() {
				return {
					"Mod-Shift-8": () => this.editor.commands.toggleBulletList(),
				}
			},
		}),
	]

	if (panels) {
		exts.push(TabsExtension, TabExtension)
	}

	// Schema only, so every reader of an article (portal, widget, live, API)
	// keeps the cards the editor wrote.
	exts.push(...CardExtensions)
	exts.push(
		...AccordionExtensions,
		...BadgeExtensions,
		...TooltipExtensions,
		...IconExtensions,
		...FieldsExtensions,
		...ExpandableExtensions,
		...UpdateExtensions,
		...TileExtensions,
		...TreeExtensions,
		...FrameExtensions,
		...MermaidExtensions,
		...PromptExtensions,
		...ColorExtensions,
	)

	if (imageGallery) {
		exts.push(ImageGalleryExtension)
	}

	if (codeBlock) {
		exts.push(CodeBlockWithLanguage)
		// A code group holds code blocks only, so the node would leave the schema
		// with a dangling content expression without them.
		if (panels) exts.push(CodeGroupExtension)
	}

	if (orderedList) {
		exts.push(
			OrderedList.extend({
				addAttributes() {
					return {
						...this.parent?.(),
						// Without this the numbered-step treatment is gone the first
						// time an article passes through the schema.
						stepsList: {
							default: null,
							parseHTML: (element: HTMLElement) =>
								element.classList?.contains("steps-list") ? "steps-list" : null,
							renderHTML: (attributes: Record<string, unknown>) => {
								if (attributes.stepsList !== "steps-list") return {}
								// The step badge counts with a CSS counter that ignores `start`.
								const start = Number(attributes.start) || 1
								return {
									class: "steps-list",
									style: start > 1 ? `counter-reset: step ${start - 1}` : null,
								}
							},
						},
					}
				},
				addKeyboardShortcuts() {
					return {
						"Mod-Shift-7": () => this.editor.commands.toggleOrderedList(),
					}
				},
			}),
		)
	}

	if (link) {
		exts.push(
			Link.extend({
				inclusive: false,
			}),
		)
	}

	if (!enableCmdEnter) {
		exts.push(DisableCommandEnter)
	}
	if (enterAsBreakeExtension) {
		exts.push(EnterAsBreakExtension.configure({}))
	}
	if (image) {
		exts.push(ImageWithLayout.configure({}))
	}
	if (inlineImage) {
		exts.push(InlineImageExtension)
	}
	if (heading) {
		exts.push(
			HeadingExtension.extend({
				addKeyboardShortcuts() {
					return {
						"Ctrl-Shift-1": () => {
							return this.editor.commands.setNode("heading", { level: 1 })
						},
						"Ctrl-Shift-2": () => {
							return this.editor.commands.setNode("heading", { level: 2 })
						},
						"Ctrl-Shift-3": () => {
							return this.editor.commands.setNode("heading", { level: 3 })
						},
					}
				},
			}),
		)
	}
	return exts
}

export { DisableCommandEnter } from "./extensions/disable-command-enter"
export { ShiftEnterParagraph } from "./extensions/shift-enter-paragraph"
export { ToggleableBlockquote } from "./extensions/toggle-blockquote"

export const HeadingExtensionWithoutH1 = HeadingExtension.extend({
	// there should only be one H1 on a page, so we render H1 in the changelog as H2
	renderHTML({ HTMLAttributes, node }) {
		return [
			node.attrs.level === 1 ? "h2" : `h${node.attrs.level}`,
			HTMLAttributes,
			0,
		]
	},
})

export const HeadingExtensionDecreaseHeadlinesSize = HeadingExtension.extend({
	// there should only be one H1 on a page, so we render H1 in the changelog as H2
	renderHTML({ HTMLAttributes, node }) {
		return [`h${node.attrs.level + 1}`, HTMLAttributes, 0]
	},
})

export {
	CodeGroupExtension,
	DEFAULT_CODE_GROUP_TITLES,
} from "./extensions/code-group-extension"
export {
	DEFAULT_TAB_TITLES,
	TabExtension,
	TabsExtension,
} from "./extensions/tabs-extension"
export { VideoExtension } from "./extensions/video-extension"
export { ImageGalleryExtension } from "./extensions/image-gallery-extension"
export { InlineImageExtension } from "./extensions/inline-image-extension"
export { PdfExtension } from "./extensions/pdf-extension"
export { FileEmbedExtension } from "./extensions/file-embed-extension"
export {
	getEmbedUrlFromLoomUrl,
	isValidLoomUrl,
	Loom,
} from "./extensions/loom-extension"

export const InlineCodeExtension = Code.configure({
	HTMLAttributes: {
		class:
			"inline-code-shadow bg-gray-100 border border-gray-200/60 dark:border-gray-800 font-medium dark:bg-gray-700/60 text-gray-900 dark:text-gray-200 font-mono text-md px-1 py-0.5 rounded-[0.2rem]",
	},
})
